import { Dispatch } from 'redux';
import {
  POLICY_INFO,
  POLICY_INFO_FAILED,
  POLICY_INFO_EXPIRED,
  POLICY_INFO_SELECT,
  POLICY_INFO_SUCCESS,
  POLICY_INFO_EDIT,
  POLICY_INFO_UNSET,
  POLICY_EDIT_ERRORS,
  POLICY_EDIT_VALUES,
  ACTIVE_ACCORDION,
  PolicyResendRequest,
  POLICY_RESEND_SUCCESS,
  POLICY_RESEND_FAILED,
  POLICY_RESEND_STARTED,
  POLICY_INITIAL_VALUES,
  DEPENDENT_EDIT_ERRORS,
  DEPENDENT_EDIT_VALUES,
  DEPENDENT_RESET_VALUES,
  DEPENDENT_INITIAL_VALUES,
  REMOVE_DEPENDENT,
  POLICY_PREMIUM_CHANGED,
  POLICY_LAYOVER_REDIRECT
} from './types';
import { ApiClient } from '../api.client';
import { ReduxStore } from '../payload-types';
import { AUTH_HEADERS, LINKS, LOGIN_ROUTES } from '@trip-ux/constants';
import { chain, filter, first, get, indexOf, isEmpty } from 'lodash';
import { PolicySearchResponse } from '../policy-search';
import { getPlanConfig } from '../recommendation';
import axios from 'axios';
import { GET_CLAIMS_SUCCESS, SET_USER } from '../user-profile';
import { LOAD_CONFIG, SALES_CHANNEL } from '../config';

// Service-profile code of the CoverMe AI Agent (Agentic Commerce). This is the
// ONLY distributor whose saved quotes the b2c CoverMe flow may accept via the
// `?quoteNum=…` hand-off deep-link. Mirrors the server-side allow-list in
// `tripx-api/configNode/*.js → QuoteRetrievalDelegations.CM_B2C`. Kept as a
// module constant instead of an import so this client-only guard doesn't
// couple to the api-side config bundle.
const CMAI_AGENT_DISTRIBUTOR_CODE = 'CMAIAgent';

// b2c CoverMe hand-off client-side allow-list. Belt-and-braces safety net
// for the server-side gate in `apps/coverme/src/app/restProxies.ts →
// policyProxyResponseHandler`. Throws a synthetic 403 (matching the shape
// the `.catch` in `getPolicy` already handles) when the retrieved quote
// wasn't originated by the CMAIAgent service profile on a B2C sales
// channel — so a foreign quote never lands in the redux store.
//
// The policy-api response DTO (`search.mapper.ts →
// PolicyResponsePayloadDto`) does NOT include `distributorCode`. It
// exposes the same underlying value via `issuedBy` when `salesChannel !== B2B`
// (b2c: issuedBy = distributorCode; b2b: issuedBy = sellerCode). We match
// on `issuedBy` and require `salesChannel === B2C` so a hypothetical b2b
// quote whose sellerCode happened to equal 'CMAIAgent' can't slip through.
const enforceB2cCmaiAgentAllowlist = (data: unknown): void => {
  const issuedBy = get(data, 'records[0].issuedBy');
  const salesChannel = get(data, 'records[0].salesChannel');
  if (salesChannel === 'B2C' && issuedBy === CMAI_AGENT_DISTRIBUTOR_CODE) {
    return;
  }
  // Throw an Error instance (SonarJS S3696) but decorate it with the
  // `.response.status/.data.errorRef` shape the downstream `.catch` in
  // `getPolicy` already narrows on. Preserves existing behaviour.
  throw Object.assign(new Error('POL_HANDOFF_FORBIDDEN'), {
    response: { status: 403, data: { errorRef: 'POL_HANDOFF_FORBIDDEN' } }
  });
};

// Extracted from the `getPolicy` `.then(payload => ...)` chain to keep the
// thunk's cognitive complexity under Sonar's threshold. When the policy we
// just loaded belongs to a different org than the current user's profile,
// switch the auth session over (CIAM vs LAN branch), refresh user/claims,
// and dispatch the new org into config. Errors are swallowed onto
// `POLICY_INFO_FAILED` — same as the previous inline behaviour.
const syncUserOrgIfChanged = async (
  userProfile: { organizationCode?: string; userType?: string },
  payload: PolicySearchResponse,
  dispatch: Dispatch
): Promise<void> => {
  const newOrg = payload.records[0].organizationCode;
  if (userProfile.organizationCode === newOrg) {
    return;
  }
  try {
    if (userProfile.userType === AUTH_HEADERS.CIAM_ID) {
      await axios.get(LOGIN_ROUTES.AUTH_SET_CIAM_USER, { headers: { newOrg } });
    } else if (userProfile.userType === AUTH_HEADERS.LAN_ID) {
      await axios.get(LOGIN_ROUTES.AUTH_SET_USER, { headers: { newOrg } });
    }
    const response = await axios.get(LOGIN_ROUTES.AUTH_GET_USER);
    const { user, claims } = response.data;
    dispatch({ type: SET_USER, payload: user });
    dispatch({ type: GET_CLAIMS_SUCCESS, payload: { claims, user } });
    dispatch({ type: LOAD_CONFIG, payload: { options: { organizationCode: newOrg } } });
  } catch (e) {
    dispatch({ type: POLICY_INFO_FAILED, payload: e });
  }
};

const retrieveOrgContent =
  (data: PolicySearchResponse) => (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient) => {
    const { organizationCode, applicationDate, salesChannel } = chain(data)
      .get('records')
      .first()
      .pick('organizationCode', 'applicationDate', 'salesChannel')
      .value();
    const lang = getState().config.locale;
    return getPlanConfig(
      organizationCode,
      new Date(applicationDate),
      lang,
      salesChannel
    )(dispatch, getState, apiClient).then(() => data);
  };

export const getAndRefreshPolicySelection =
  (policyNumbers: string[]) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    dispatch({ type: POLICY_INFO });
    return apiClient(dispatch, getState)
      .get(`${LINKS.POLICY}/${first(policyNumbers)}`, { headers: { 'associated-policy': true } })
      .then(payload => {
        dispatch({
          type: POLICY_INFO_SELECT,
          payload: filter(
            get(payload, 'records', []),
            pol => indexOf(policyNumbers, get(first(pol.paxes), 'policyNumber')) !== -1
          )
        });
        dispatch({
          type: POLICY_INFO_SUCCESS,
          payload
        });
      })
      .catch(e => {
        dispatch({ type: POLICY_INFO_FAILED, payload: e });
      });
  };

export const resetPolicyInfo =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({ type: POLICY_INFO });
  };

export const setPolicyPremiumChanged =
  (premiumChanged: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({ type: POLICY_PREMIUM_CHANGED, payload: premiumChanged });
  };

export const setPolicyLayoverRedirect =
  (layoverRedirect: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({ type: POLICY_LAYOVER_REDIRECT, payload: layoverRedirect });
  };

export const getPolicy =
  (policyNumber: string, isQuote?: boolean) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const state = getState();
    const userProfile = state.userProfile;

    dispatch({ type: POLICY_INFO });
    return apiClient(dispatch, getState)
      .get(`${LINKS.POLICY}/${policyNumber}${isQuote ? '?status=QUOTE' : ''}`, {
        headers: { 'associated-policy': true }
      })
      .then(data => {
        // A 200 OK with an empty `records` array means the requested quote is
        // no longer available (expired/purged or the quoteNum was invalid).
        // Treat it as an expired quote so the StepGuard hand-off flow cleanly
        // redirects the user back to Traveller Details instead of letting
        // downstream code crash on `payload.records[0].organizationCode`
        // (which surfaces as the generic ErrorBoundary "Something went wrong"
        // page).
        if (isEmpty(get(data, 'records'))) {
          throw { response: { status: 410, data: { errorRef: 'POL_QUOTE_EXPIRED' } } };
        }
        const salesChannel = get(getState(), 'config.options.salesChannel');
        const isB2C = salesChannel === SALES_CHANNEL.B2C;
        // b2c CoverMe hand-off may only load quotes originated by the
        // CMAIAgent service profile (Agentic Commerce). Two enforcement
        // layers exist; this is the *second* one (UI safety net):
        //   Layer 1 — SERVER (primary, closes the data leak):
        //     `apps/coverme/src/app/restProxies.ts → policyProxyResponseHandler`
        //     buffers the upstream `/api/policies/QTE...?status=QUOTE`
        //     response, inspects `records[0].distributorCode`, and rewrites
        //     the body to a stripped 403 (`POL_HANDOFF_FORBIDDEN`) BEFORE any
        //     byte reaches the browser. That's the byte that must never leak
        //     — a raw upstream body would be readable from DevTools → Network
        //     even if this client thunk refuses to dispatch it.
        //   Layer 2 — CLIENT (this block, belt-and-braces):
        //     If the server layer ever regresses (or a future refactor
        //     removes selfHandleResponse), the redux thunk still refuses to
        //     land a foreign quote in the customer store — seller/distributor/
        //     sponsor/salesChannel from another party would corrupt the
        //     eventual policy sale. Only applies to the hand-off path
        //     (`isQuote === true`); the b2c post-payment Confirmation flow
        //     calls `getPolicy(policyNum)` without `isQuote` for the
        //     visitor's just-created policy and must not be blocked.
        //     Failure is dispatched via the standard error path so
        //     StepGuard's `handOffFailed` derivation kicks in and cleanly
        //     redirects the visitor to TravellerDetails.
        if (isB2C && isQuote) {
          enforceB2cCmaiAgentAllowlist(data);
        }
        // `retrieveOrgContent` fires the `ORGANIZATION_CONTRACT_WITH_CONFIG_QUERY`
        // GraphQL call whose response populates `recommendation.productConfigs`.
        // That slice is consumed exclusively by the admin Policy Endorsement
        // forms (`maintenance/endorsement/endorse-policies/**`), which run on
        // the b2b (admin) sales channel. On b2c (CoverMe customer flow) the
        // slice has no consumer, and the b2c `/graphql` proxy allow-list also
        // rejects the query with 404 (only `ORGANIZATION_CONTRACT_QUERY`,
        // `TRAVEL_LOCATIONS_QUERY`, `LOCATION_QUERY` are whitelisted). Skip
        // the prefetch on b2c to eliminate a wasted round-trip + 404 noise on
        // every agent hand-off (`?quoteNum=…` deep-link).
        if (isB2C) {
          return data as unknown as PolicySearchResponse;
        }
        return retrieveOrgContent(data as unknown as PolicySearchResponse)(dispatch, getState, apiClient);
      })
      .then(async payload => {
        //check org in profile - compare to user and switch if necessary
        await syncUserOrgIfChanged(userProfile, payload, dispatch);
        dispatch({
          type: POLICY_INFO_SUCCESS,
          payload
        });
      })
      .catch(e => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        const errorRef = (e as { response?: { data?: { errorRef?: string } } })?.response?.data?.errorRef;
        if (status === 410 || errorRef === 'POL_QUOTE_EXPIRED') {
          dispatch({ type: POLICY_INFO_EXPIRED });
        }
        dispatch({ type: POLICY_INFO_FAILED, payload: e });
      });
  };

export const resendConfirmation =
  (payload: PolicyResendRequest) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    dispatch({ type: POLICY_RESEND_STARTED });
    return apiClient(dispatch, getState)
      .post(LINKS.POLICY_RESEND, payload)
      .then(() => {
        dispatch({ type: POLICY_RESEND_SUCCESS });
      })
      .catch(e => {
        dispatch({ type: POLICY_RESEND_FAILED, payload: e });
      });
  };

export const unsetPolicies =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_INFO_UNSET
    });
  };

export const selectPolicies =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_INFO_SELECT,
      payload
    });
  };

export const editPolicies =
  (payload: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_INFO_EDIT,
      payload
    });
  };

export const editErrors =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_EDIT_ERRORS,
      payload
    });
  };

export const editValues =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_EDIT_VALUES,
      payload
    });
  };

export const setInitialValues =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: POLICY_INITIAL_VALUES,
      payload
    });
  };

export const activeAccordion =
  (payload: number) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: ACTIVE_ACCORDION,
      payload
    });
  };

export const setDependentInitials =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: DEPENDENT_INITIAL_VALUES,
      payload
    });
  };

export const setDependentErrors =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: DEPENDENT_EDIT_ERRORS,
      payload
    });
  };
export const setDependentEditValues =
  (payload: unknown) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: DEPENDENT_EDIT_VALUES,
      payload
    });
  };

export const resetDependentEditValues =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: DEPENDENT_RESET_VALUES
    });
  };

export const removeDependent =
  (payload: string) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: REMOVE_DEPENDENT,
      payload
    });
  };
